import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EnvironmentService } from '@core/services/environment.service';
import { LoginRedirectService } from '@core/services/login-redirect.service';
import { SessionService } from '@core/services/session.service';
import { catchError, of, switchMap, throwError } from 'rxjs';

/**
 * Renews a lapsed session. The cookies are attached by the browser, so a 401 is
 * the only thing left to handle: renew once and replay.
 */
export const authInterceptor: HttpInterceptorFn = (request, next) => {
    const sessionService = inject(SessionService);
    const loginRedirectService = inject(LoginRedirectService);
    const router = inject(Router);
    const environmentService = inject(EnvironmentService);

    // `skip` marks the session endpoints themselves, where renewing would recurse.
    const isSessionEndpoint = request.headers.has('skip');
    const prepared = request.clone({
        headers: request.headers.delete('skip'),
        setHeaders: {
            'FA-Client-Version': environmentService.getVersion(),
        },
    });

    return next(prepared).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== 401 || isSessionEndpoint) {
                return throwError(() => error);
            }
            return sessionService.renew().pipe(
                // Ahead of the replay, so a failure of the replayed request
                // propagates as itself instead of ending the session.
                catchError((renewalError: HttpErrorResponse) => {
                    // Only a rejected refresh cookie means the session is over.
                    // A 504 or a dropped connection says nothing about it.
                    if (renewalError.status !== 401) {
                        return throwError(() => renewalError);
                    }
                    // Guards only run on navigation, and the session is over
                    // whether or not the cookies can be cleared.
                    router.navigateByUrl(
                        loginRedirectService.remember(
                            router.routerState.snapshot
                        )
                    );
                    return sessionService.logout().pipe(
                        catchError(() => of(undefined)),
                        switchMap(() => throwError(() => renewalError))
                    );
                }),
                switchMap(() => next(prepared))
            );
        })
    );
};
